import { fetchCommercialSearchPredictions } from 'data/commercial/search/fetcher';
import { SearchStrategyProps, SearchResult } from './SearchStrategy';
import { BaseSearchStrategy } from './BaseSearchStrategy';
import SearchPrediction from 'data/search-predictions';
import { SearchPredictionSource } from 'utils/google-maps/types';
import { getCommercialGooglePredictions } from 'utils/google-maps';
import { processPredictionsForCommercial } from './utils';
import type { CommercialSearchPredictionsParams } from 'data/commercial/search/types';
import { GoSearchPredictionData } from 'data/search/predictions/types';
import { Point } from 'data/listing';

const priorityLabels = ['zip', 'state'];
export class CommercialSearchStrategy extends BaseSearchStrategy {
  /**
   * Override the base search method to use commercial search + Google integration
   * Note: Commercial listings search is US-only
   */
  async search(props: SearchStrategyProps): Promise<SearchResult> {
    try {
      const { queryString } = props;

      // Get commercial search predictions from go-search
      const commercialSearchParams: CommercialSearchPredictionsParams = {
        query: queryString.toLowerCase(),
        limit: 10, // Reduced to make room for Google results
      };

      const commercialSearchPredictions = await fetchCommercialSearchPredictions(commercialSearchParams);
      // Convert commercial predictions to SearchPrediction format if available
      let commercialPredictions: SearchPrediction[] = [];
      if (commercialSearchPredictions?.predictions) {
        commercialPredictions = commercialSearchPredictions.predictions.map((prediction, _index) => {
          // Convert commercial prediction to SearchPrediction format
          // Transform the position data to match GoSearchPredictionData structure
          let data: GoSearchPredictionData['attributes']['data'];

          if (prediction.location.position.coordinates) {
            const [longitude, latitude] = prediction.location.position.coordinates;
            data = {
              position: new Point({
                coordinates: [longitude, latitude],
                type: 'Point',
              }),
            };
          }

          const convertedPrediction: GoSearchPredictionData = {
            id: prediction.id,
            type: prediction.group,
            attributes: {
              description: prediction.description,
              group: prediction.group,
              label: prediction.label as any, // Type assertion to handle the mismatch
              data,
              url_path: prediction.slug,
            },
          };

          const searchPrediction = new SearchPrediction(convertedPrediction, SearchPredictionSource.GO_SEARCH);

          return searchPrediction;
        });
      }

      // Get Google predictions for location suggestions (US only)
      let googlePredictions: SearchPrediction[] = [];
      try {
        const allGooglePredictions = await getCommercialGooglePredictions(queryString);

        // Prioritize states and zip codes, then fill remaining slots with other predictions
        const priorityPredictions = allGooglePredictions.filter(
          prediction => priorityLabels.includes(prediction.label)
        );
        const otherPredictions = allGooglePredictions.filter(
          prediction => !priorityLabels.includes(prediction.label)
        );

        const remainingSlots = Math.max(0, 5 - priorityPredictions.length);
        googlePredictions = [
          ...otherPredictions.slice(0, remainingSlots),
          ...priorityPredictions,
        ];
      } catch (error) {
        console.warn('Failed to fetch Google predictions for commercial search:', error);
        googlePredictions = [];
      }

      const finalPredictions = processPredictionsForCommercial(
        commercialPredictions,
        googlePredictions
      );

      return {
        searchPredictions: finalPredictions,
        searchAgentPredictions: [],
      };
    } catch (error) {
      console.error('Commercial search strategy failed:', error);
      return {
        searchPredictions: [],
        searchAgentPredictions: [],
      };
    }
  }

  /**
   * This method is no longer used since we override the search method
   */
  protected async processSearchResults(_goSearchPredictions: SearchPrediction[], props: SearchStrategyProps): Promise<SearchResult> {
    // This method is deprecated for CommercialSearchStrategy
    return this.search(props);
  }
}